import * as React from 'react'
import update, { Spec } from 'immutability-helper'
import { ACTION_NAMES, DUMB_COMPONENT_TYPES, FlowFormElement, OnMove, ELEMENT_TYPES, FORM_ELEMENT_TYPES, NewButton, EditTable, Input, RowElement, NotificationType, getElementName } from 'prace-common-components'
import { $ModalContainer, $ButtonContainer } from '../styles'
import { EditGroupElementProps } from './types'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { getElementUsage } from 'util/getElementUsage'
import { RootState } from 'store'

const defaultElement = {
  type: ELEMENT_TYPES.ELEMENT,
  elementType: FORM_ELEMENT_TYPES.TEXT,
  description: '',
  // New fields added to a group are mandatory by default; can be unchecked in the element editor.
  // Persisted/read via config.required (the API drops any top-level `required`).
  config: {
    required: true,
    autofill: null,
    decimal: false,
    min: null,
    max: null,
    exportAlias: null,
    hidden: false,
    previewIds: [],
  },
  title: 'Label',
  permissions: {
    inherit: false,
    export: [],
    visibility: [],
  },
  value: '',
  flow: [],
  effects: [],
  automations: [],
}

export const EditGroupElement: React.FC<EditGroupElementProps> = (
  { call, element, saveHandler, cancelHandler, loading },
) => {
  const dispatch = useAppDispatch()
  const storeTemplates = useAppSelector((state: RootState) => state.calls.templates)
  const [groupName, setGroupName] = React.useState<string>(element.title)
  const [elements, setElements] = React.useState<FlowFormElement[]>(element.flow)

  const rows: RowElement[] = elements as RowElement[]

  // TODO: Add debounce
  const onMove = ({
    dragIndex,
    hoverIndex,
  }: OnMove) => {
    setElements((prevElements) => {
      const newItems =  update(prevElements, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, prevElements[dragIndex]],
        ]}).map((el: FlowFormElement, i: number) => ({ ...el, order: i }))
      return newItems
    },
    )
  }

  const onDeleteElement = (ids: string[]) => {
    /* Rows added but not yet saved carry a placeholder id, so only the fields the group already has on the
       server are looked up */
    const savedIds = new Set((element.flow || []).map((field) => field.id))
    const deletedIds = ids.map(Number).filter((id) => savedIds.has(id))
    /* Checked as a set, so conditions between the fields being deleted together are not blocking */
    const {
      automations,
      conditions,
      templates,
    } = getElementUsage(deletedIds, call, storeTemplates || [])
    /* The group has unsaved changes, so the blockers are named instead of linked to */
    const blockers = [
      ...conditions.map((condition) => `a condition on "${getElementName(condition.ownerId, call)}"`),
      ...automations.map((automation) => `the automation "${ACTION_NAMES[automation.type]}"`),
      ...templates.map((template) => `the email template "${template.title}"`),
    ]
    if(blockers.length) {
      dispatch({ type: 'notification', payload: { type: NotificationType.error,
        msg: `Cannot delete: still used by ${[...new Set(blockers)].join(', ')}` } })
      return
    }
    setElements((prevElements) => prevElements.filter((el) => !ids.includes(String(el.id))))
  }

  const onAddElement = () => {
    /* Negative placeholder ids never collide with real flow elements or with each other */
    setElements((prevElements) => update(prevElements, {
      $push: [{
        ...defaultElement,
        parentId: element.id,
        id: Math.min(0, ...prevElements.map((el) => el.id)) - 1,
        order: prevElements.length,
      }],
    }))
  }

  const onChangeElement = React.useCallback((name: string, value: Nullable<StringNumber>, rowId: string | number) => {
    setElements((prevElements) => {
      const index = prevElements.findIndex((element) => element.id == rowId)
      /* Switching to a display-only type clears the (meaningless) required flag; switching to a real
        input type keeps whatever the field already had (so the mandatory-by-default stays for new fields). */
      if(name === 'elementType') {
        const supportsRequired = !DUMB_COMPONENT_TYPES.includes(value as FORM_ELEMENT_TYPES)
        const elementSpec: Spec<FlowFormElement> = { elementType: { $set: value as FORM_ELEMENT_TYPES } }
        /* config may be absent (left join); only reconcile required when it exists to avoid a $merge on undefined */
        if(prevElements[index].config)
          elementSpec.config = { $merge: { required: supportsRequired && !!prevElements[index].config?.required } }
        return update(prevElements, { [index]: elementSpec })
      }
      return update(prevElements, {
        [index]: {
          [name]: { $set: value as string | FORM_ELEMENT_TYPES },
        }})})
  }, [])

  const onSave = () => {
    if(!elements.length || !groupName) {
      dispatch({ type: 'notification', payload: { type: NotificationType.error, msg: 'Groups must have elements and a name' } })
      return
    }
    saveHandler(elements, groupName)
  }

  const groupNameHandler = (_name: string, value: Nullable<StringNumber>) => {
    setGroupName(value as string)
  }

  return (
    <$ModalContainer width={800}>
      <Input title='Group name' value={groupName} onChange={groupNameHandler} />
      <EditTable
        rows={rows}
        onMove={onMove}
        onAddRow={onAddElement}
        onRemoveRow={onDeleteElement}
        onChangeRow={onChangeElement}
      />
      <$ButtonContainer container justifyContent='space-between'>
        <NewButton variant='contained' loading={loading} onClick={onSave}>
          Save Changes
        </NewButton>
        <NewButton variant='outlined' error disabled={loading} onClick={cancelHandler}>
          Cancel
        </NewButton>
      </$ButtonContainer>
    </$ModalContainer>
  )
}
